import { useEffect, useState } from 'react';
import type { FulfillmentAdminClient, OrderComment } from '@/infrastructure/FulfillmentAdminClient';
import { lineMemos } from '../commentNote';
import { LineMemoThread } from './LineMemoThread';

interface ScannedLineCommentsProps {
  client: Pick<FulfillmentAdminClient, 'listOrderComments'>;
  orderId: number;
  lineNo: number;
}

interface CommentRead {
  orderId: number;
  lineNo: number;
  comments?: OrderComment[];
  failed?: boolean;
}

/** Read the matched order's line, not every note about the scanned SKU across other orders. */
export function ScannedLineComments({ client, orderId, lineNo }: ScannedLineCommentsProps) {
  const [read, setRead] = useState<CommentRead>();
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setRead(undefined);
    void client.listOrderComments(orderId).then(
      ({ comments }) => {
        if (active) {
          setRead({ orderId, lineNo, comments: lineMemos(comments, lineNo) });
        }
      },
      () => {
        if (active) {
          setRead({ orderId, lineNo, failed: true });
        }
      },
    );
    return () => {
      active = false;
    };
  }, [client, orderId, lineNo, retry]);

  // A new target must never paint the previous target's notes, even before its effect runs.
  const current = read?.orderId === orderId && read.lineNo === lineNo ? read : undefined;
  return (
    <section className="scan-line-comments" aria-label={`Comments on order ${orderId} line ${lineNo}`}>
      <b>
        Line comments · Order {orderId} · Line {lineNo}
      </b>
      {!current && <div className="mut">Loading line comments…</div>}
      {current?.failed && (
        <div>
          Could not load line comments.{' '}
          <button type="button" className="btn btn-line btn-sm" onClick={() => setRetry((value) => value + 1)}>
            Retry comments
          </button>
        </div>
      )}
      {current?.comments &&
        (current.comments.length > 0 ? (
          <LineMemoThread comments={current.comments} />
        ) : (
          <div className="mut">No comments on this line.</div>
        ))}
    </section>
  );
}
